import React, { createContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';

export const PublicitatContext = createContext();

const setupSmart = async (setSetupPubliFinished, sasSetupCalled) => {
    // Verificar si estamos en el lado del cliente
    if (typeof window === 'undefined') {
        return;
    }

    // Verificar si estamos en un entorno de testing (Cypress)
    if (window.Cypress || process.env.NODE_ENV === 'test') {
        console.log('Skipping SAS setup in testing environment');
        setSetupPubliFinished(true);
        return;
    }

    // Verificar si ya se ha llamado sas.setup para evitar el error "sas.setup can only be called once"
    if (sasSetupCalled.current) {
        return;
    }

    // Verificar si SAS ya ha sido configurado globalmente
    if (window.__SAS_SETUP_CALLED) {
        setSetupPubliFinished(true);
        return;
    }

    let sasSetup = {
        networkid: 1029, 
        domain: "https://www3.smartadserver.com", 
        async: true, 
        renderMode: 2
    }

    /* Mirem si tenim la cookie informada per les apps per agafar el uid, appName i el built*/
    if(window.CCMAStorage){
        const cookieSmartPlain = await window.CCMAStorage.get('CCMA_Smart_Apps');
        try {
            if(cookieSmartPlain) {
                const cookieSmart = JSON.parse(cookieSmartPlain);
                sasSetup.uid = cookieSmart.uid;
                sasSetup.appName = cookieSmart.appName;
                sasSetup.bundleId = cookieSmart.bundleId;
            }
        } catch(e) {
            console.log("publicitat - cookieSmartPlain no te el format esperat");
        }
    }
    
    // Marcar que estamos a punto de llamar sas.setup
    sasSetupCalled.current = true;
    window.__SAS_SETUP_CALLED = true;
    
    window.sas?.cmd?.push(function() {
        try {
            window.sas.setup(sasSetup);
            setSetupPubliFinished(true);
        } catch (error) {
            console.warn('SAS setup error (possibly already called):', error.message);
            setSetupPubliFinished(true); // Marcar como completado incluso si falla
        }
    });
};

const findPublicitatNode = (children) => {
    if (!children || !children.props || !Array.isArray(children.props.children)) {
        return false;
    }

    //Buscar "Publicitat" directament al children
    const hasPublicitatDirectly = children.props.children.some(child =>
        child?.props?.layout?.structure?.some(item => item.name === "Publicitat")
    );

    if (hasPublicitatDirectly) return true;

    //Si no està directament, buscar dins del "Contenidor"
    return children.props.children.some(child =>
        child?.props?.layout?.structure?.some(contenidor =>
            contenidor.name === "Contenidor" &&
            Array.isArray(contenidor.children) &&
            contenidor.children.some(subChild => subChild.name === "Publicitat")
        )
    );
};



const PublicitatProvider = (props) => {
    const { children } = props;
    const [setupPubliFinished, setSetupPubliFinished] = useState(false);
    const sasSetupCalled = useRef(false);
    const hasTopBannerPublicitat = useRef({
        publiFounded: false, // Valor inicial
        SCROLL_THRESHOLD: 3  // Valor inicial basado en publiFounded
    });

    // Memoizar la función de setup para evitar recreaciones
    const _setupSmart = useCallback(async () => {
        await setupSmart(setSetupPubliFinished, sasSetupCalled);
    }, []);

    // Memoizar la detección de publicidad para evitar cálculos innecesarios
    const publiFounded = useMemo(() => {
        return findPublicitatNode(children);
    }, [children]);

    // Actualizar hasTopBannerPublicitat solo cuando publiFounded cambie
    useEffect(() => {
        const newValue = { publiFounded, SCROLL_THRESHOLD: publiFounded ? 264 : 3 };
        
        // Solo actualizar si realmente ha cambiado
        if (hasTopBannerPublicitat.current.publiFounded !== publiFounded) {
            hasTopBannerPublicitat.current = newValue;
        }
    }, [publiFounded]);

    // Setup inicial - solo una vez
    useEffect(() => {
        _setupSmart();
    }, [_setupSmart]);

    // Memoizar el valor del contexto para evitar renders innecesarios
    const contextValue = useMemo(() => ({
        setupPubliFinished,
        hasTopBannerPublicitat
    }), [setupPubliFinished]);

    return (
        <PublicitatContext.Provider value={contextValue}>
            {children}
        </PublicitatContext.Provider>
    );
};

export default React.memo(PublicitatProvider);
